import React, { useEffect, useState } from 'react';
import { TCN, TCN_COLOR_MAP, autoRatioColor } from './tokens';

/**
 * TCN ProgressBar — auto color rule based on value/target ratio:
 *   green ≥100% / amber 80–99% / red <80%
 * Override with `color` prop (semantic key or CSS color).
 *
 * Props:
 *  - value: number
 *  - target: number (default 100)
 *  - height: px (default 6)
 *  - animated: boolean (default true) — animates from 0 → width on mount
 *  - color: optional override — overrides the auto rule
 *  - showCap: boolean — draws a 1px target tick at 100% (default false)
 */
const ProgressBar = ({
  value = 0,
  target = 100,
  height = 6,
  animated = true,
  color,
  showCap = false,
  className = '',
  style,
}) => {
  const ratio = target > 0 ? Math.max(0, Math.min(value / target, 1.2)) : 0;
  const semantic = color ?? autoRatioColor(value, target);
  const fill = TCN_COLOR_MAP[semantic] ?? semantic;

  const [width, setWidth] = useState(animated ? 0 : ratio);
  useEffect(() => {
    if (!animated) {
      setWidth(ratio);
      return;
    }
    const raf = requestAnimationFrame(() => setWidth(ratio));
    return () => cancelAnimationFrame(raf);
  }, [animated, ratio]);

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={`relative w-full overflow-hidden ${className}`}
      style={{
        height,
        background: TCN.surface3,
        borderRadius: TCN.radius.pill,
        ...style,
      }}
    >
      <div
        style={{
          width: `${Math.min(width, 1) * 100}%`,
          height: '100%',
          background: fill,
          borderRadius: TCN.radius.pill,
          // mockup ui-kit.jsx:211 — transition 1.2s, no glow shadow
          transition: `width 1.2s ${TCN.ease}`,
        }}
      />
      {showCap && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            insetInlineStart: '100%',
            width: 1,
            background: TCN.borderStrong,
            marginInlineStart: -1,
          }}
        />
      )}
    </div>
  );
};

export default ProgressBar;
